import { useState, useEffect, useRef } from 'react'

/**
 * Decode JWT token payload
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null

    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    const decoded = JSON.parse(atob(payload))

    return decoded
  } catch (error) {

    return null
  }
}

/**
 * Check if token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  const now = Date.now() / 1000
  return decoded.exp < now
}

/**
 * Get token expiration date
 */
export const getTokenExpiration = (token) => {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return null

  return new Date(decoded.exp * 1000)
}

/**
 * React hook to manage token expiration
 * Returns time remaining and expiration status
 */
export const useTokenExpiration = (token, onExpire, intervalMs = 30000) => {
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [totalTokenTime, setTotalTokenTime] = useState(null)
  const [isExpiring, setIsExpiring] = useState(false)

  const totalTimeRef = useRef(null)
  const expirationTimeRef = useRef(null)

  useEffect(() => {
    if (!token) {
      setTimeRemaining(null)
      setTotalTokenTime(null)
      setIsExpiring(false)
      return
    }

    const decoded = decodeToken(token)
    if (!decoded || !decoded.exp) return

    const expirationTime = decoded.exp * 1000
    expirationTimeRef.current = expirationTime

    // Calculate total time on first load for this token
    if (!totalTimeRef.current) {
      const now = Date.now()
      const totalTime = expirationTime - now

      if (totalTime > 0) {
        totalTimeRef.current = totalTime
        setTotalTokenTime(totalTime)
      }
    } else {
      setTotalTokenTime(totalTimeRef.current)
    }

    const checkExpiration = () => {
      const now = Date.now()
      const remaining = expirationTimeRef.current - now
      const remainingMinutes = remaining / 60000

      setTimeRemaining(remaining)
      setIsExpiring(remainingMinutes <= 5) // Alert when 5 minutes remaining

      // Auto-logout on expiration
      if (remaining <= 0 && onExpire) {
        onExpire()
      }
    }

    checkExpiration()
    // 30s by default to reduce re-renders; callers showing a live countdown pass a shorter interval
    const interval = setInterval(checkExpiration, intervalMs)

    return () => clearInterval(interval)
  }, [token, onExpire, intervalMs])

  return {
    timeRemaining,
    totalTokenTime,
    isExpiring,
    isExpired: timeRemaining !== null && timeRemaining <= 0
  }
}

/**
 * Format time remaining to readable string
 */
export const formatTimeRemaining = (milliseconds) => {
  if (!milliseconds || milliseconds <= 0) return '0:00'

  const totalSeconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}
